import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtNum } from "../lib/api";
import { StatusBadge, Section, Empty } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Bot, Pause, Play, X } from "lucide-react";

function StartDialog({ list, onClose, onStarted }) {
  const [est, setEst] = useState(null);
  const [maxCompanies, setMaxCompanies] = useState("");
  const [maxCredits, setMaxCredits] = useState("");
  useEffect(() => {
    if (!list) return;
    setEst(null);
    api.post("/research/estimate", { list_id: list.id }).then((r) => { setEst(r.data); setMaxCredits(String(r.data.max_credits_default)); });
  }, [list]);
  const start = async () => {
    try {
      await api.post("/research/start", { list_id: list.id, max_companies: maxCompanies ? Number(maxCompanies) : null, max_credits: Number(maxCredits) || null });
      toast.success("Agent de recherche lancé");
      onClose(); onStarted();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const n = maxCompanies ? Math.min(Number(maxCompanies), est?.pending || 0) : est?.pending || 0;
  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="research-start-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Recherche des décideurs</DialogTitle></DialogHeader>
        {!est || !list ? <div className="text-sm text-slate-500">Estimation…</div> : (
          <div className="space-y-4 text-sm">
            <div>Liste <b>{list.name}</b> · <span className="tabular">{fmtNum(est.pending)}</span> entreprises à analyser.</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {["apollo", "rocketreach"].map((p) => <div key={p} className={`border rounded px-3 py-2 ${est.providers[p] ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{p === "apollo" ? "Apollo.io" : "RocketReach"} · {est.providers[p] ? "configuré" : "non configuré"}</div>)}
              <div className={`border rounded px-3 py-2 ${est.zerobounce ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50 text-slate-500"}`}>ZeroBounce · {est.zerobounce ? "configuré" : "optionnel"}</div>
            </div>
            <div className="text-xs text-slate-600">Ordre : {est.priority.join(" → ")} · Fonctions : {est.titles.join(", ")}</div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs" data-testid="research-estimate">
              Estimation pour {fmtNum(n)} entreprises : entre <b>{fmtNum(n)}</b> et <b>{fmtNum(n * 4)}</b> crédits d'enrichissement (au plus 2 profils par fournisseur), et jusqu'à <b>{fmtNum(n * 2)}</b> validations ZeroBounce. Les recherches Apollo (api_search) ne consomment généralement pas de crédit d'email ; vérifiez les conditions de votre abonnement.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Nombre max d'entreprises (optionnel)</Label><Input type="number" value={maxCompanies} onChange={(e) => setMaxCompanies(e.target.value)} data-testid="research-max-companies" /></div>
              <div className="space-y-1"><Label className="text-xs">Plafond de crédits pour cette session</Label><Input type="number" value={maxCredits} onChange={(e) => setMaxCredits(e.target.value)} data-testid="research-max-credits" /></div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={start} disabled={!est || !est.pending} data-testid="research-start-confirm">Lancer la recherche</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ResearchPanel({ lists }) {
  const [jobs, setJobs] = useState([]);
  const [target, setTarget] = useState(null);
  const load = () => api.get("/research/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);
  const act = async (id, a) => { try { await api.post(`/research/jobs/${id}/${a}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <Section title="Lancer l'agent IA sur une liste" description="Priorité : CEO → Founder / Co-Founder → President → CFO. Apollo d'abord, RocketReach en complément. Un seul décideur retenu par entreprise.">
        {lists.length === 0 ? <div className="text-sm text-slate-500">Créez ou importez une liste d'abord.</div> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {lists.map((l) => (
              <div key={l.id} className="border border-slate-200 rounded-md p-4 flex items-center justify-between">
                <div><div className="font-medium text-sm">{l.name}</div><div className="text-xs text-slate-500 tabular">{fmtNum(l.companies)} entreprises · {fmtNum(l.with_contact)} avec décideur</div></div>
                <Button size="sm" variant="outline" onClick={() => setTarget(l)} data-testid={`research-list-${l.id}`}><Bot className="h-4 w-4 mr-1" />Rechercher</Button>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Sessions de recherche" description="Résultats et crédits API consommés">
        {jobs.length === 0 ? <Empty title="Aucune session" /> : (
          <div className="space-y-3" data-testid="research-jobs">
            {jobs.map((j) => {
              const total = Object.entries(j.credits || {}).filter(([k]) => !k.endsWith("_search")).reduce((a, [, v]) => a + v, 0);
              return (
                <div key={j.id} className="border border-slate-200 rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3"><span className="font-medium text-sm">{j.list_name}</span><StatusBadge status={j.status} /><span className="text-xs text-slate-400">{fmtDate(j.created_at)}</span></div>
                    <div className="flex gap-1">
                      {j.status === "running" && <Button size="sm" variant="outline" onClick={() => act(j.id, "cancel")} data-testid={`job-cancel-${j.id}`}><X className="h-4 w-4 mr-1" />Arrêter</Button>}
                      {j.status === "paused" && <Button size="sm" variant="outline" onClick={() => act(j.id, "resume")} data-testid={`job-resume-${j.id}`}><Play className="h-4 w-4 mr-1" />Reprendre</Button>}
                    </div>
                  </div>
                  {j.status === "running" && <Progress className="h-1 mt-3" value={j.max_companies < 1e9 ? (100 * j.processed) / j.max_companies : 50} />}
                  {j.pause_reason && <div className="text-xs text-amber-800 mt-2 flex items-center gap-1"><Pause className="h-3 w-3" />{j.pause_reason}</div>}
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3 text-xs">
                    {[["Traitées", j.processed], ["Trouvés (fiables)", j.found], ["Trouvés non fiables", j.found_unverified], ["Sans contact", j.not_found], ["Erreurs", j.errors], ["Crédits utilisés", total]].map(([k, v]) => (
                      <div key={k}><div className="eyebrow">{k}</div><div className="tabular text-sm">{fmtNum(v)}</div></div>
                    ))}
                  </div>
                  {total > 0 && <div className="text-[11px] text-slate-500 mt-2">Détail : {Object.entries(j.credits).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>}
                </div>
              );
            })}
          </div>
        )}
      </Section>
      <StartDialog list={target} onClose={() => setTarget(null)} onStarted={load} />
    </div>
  );
}
